import React, { useState } from 'react';
import { grey } from '@mui/material/colors';
import { Add, Remove } from '@mui/icons-material';
import { CircularProgress, IconButton, Stack, Typography } from '@mui/material';

import { MUITable, MUIBaseEntity } from 'components/shared/MUITable';
import { Template } from '../../api';
import { Department } from '../../types';
import { createColumns } from './createColumns';
import { capitalizeFirstLetter } from '../../../../formatters';

export interface ListSectionActions {
  onCopyTemplate: (templateId: string) => void;
  onEditTemplate: (templateId: string) => void;
  onRedirectToCloseReasons: (templateId: string) => void;
  onSetActiveStatus: (templateId: string) => Promise<void>;
}

export type TemplateRow = MUIBaseEntity & {
  id: string;
  name: string;
  isActive: 'true' | 'false';
  createdDateTime: string;
  modifiedDateTime: string | null;
};

interface Props {
  renderIndex: number;
  templates: Template[];
  department: Department;
  actions: ListSectionActions;
  isActiveStatusSetting: boolean;
}

export const ListSection = ({ department, templates, actions, renderIndex, isActiveStatusSetting }: Props) => {
  const [isCollapsed, setIsCollapsed] = useState<boolean>(renderIndex <= 1);

  const columns = createColumns(actions, isActiveStatusSetting);

  const tableData: TemplateRow[] = templates.map(template => ({
    name: template.name,
    id: template.businessProcessFlowId,
    createdDateTime: template.createdDateTime,
    isActive: template.isActive ? 'true' : 'false',
    modifiedDateTime: template.modifiedDateTime ?? null,
    paymentType: template.applyRules?.paymentType ?? '',
    applyingFor: template.applyRules?.applyingFor ?? '',
    equipmentCondition: template.applyRules?.equipmentCondition ?? '',
  }));

  return (
    <Stack gap="0.2rem">
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ borderRadius: '0.25rem', padding: '0.25rem 0.5rem', backgroundColor: grey[200] }}
      >
        <Stack direction="row" alignItems="center">
          <IconButton
            size="small"
            onClick={() => {
              setIsCollapsed(prev => !prev);
            }}
          >
            {!isCollapsed ? <Add /> : <Remove />}
          </IconButton>

          <Typography variant="subtitle1" component="span" fontWeight="bold">
            {capitalizeFirstLetter(department.name)}
          </Typography>

          {isActiveStatusSetting && <CircularProgress size={20} />}
        </Stack>
      </Stack>

      {isCollapsed && <MUITable<TemplateRow> columns={columns} tableData={tableData} isLoading={false} />}
    </Stack>
  );
};
